/*
 * File: AddDeliveryAddressModal.tsx
 * Project: papa-bear
 * -----
 * Last Modified: 17 Jan 2022 10:51:18 am
 * -----
 * Copyright (c) 2022 Elegant Software Solutions All rights reserved.
 */

import { PlusOutlined } from '@ant-design/icons';
import { FetchBaseQueryError } from '@reduxjs/toolkit/dist/query';
import { Alert, Col, Divider, Form, Input, Modal, notification, Row, Switch, Typography } from 'antd';
import { useFormikContext } from 'formik';
import { SalesOrderPayloadModel } from 'models/SalesOrderPayloadModel';
import { StringErrorModel } from 'models/StringErrorModel';
import { useState } from 'react';
import { useSelector } from 'react-redux';
import { ReduxState } from 'redux/rootReducer';
import { useCreateDeliveryAddressMutation } from 'redux/services/useBusterTheHorseService';
import { DeliveryAddressCountrySelector } from './DeliveryAddressCountrySelector';
import { DeliveryAddressStateSelector } from './DeliveryAddressStateSelector';
import { GoogleAutoComplete } from './GoogleAutoComplete';

/* ****************** Interfaces ****************** */
interface Props {
  show: boolean;
  onClose: () => void;
}

interface FormModel {
  city: string;
  country: string;
  description: string;
  state: string;
  street: string;
  zipCode: string;
}

const formItemLayout = {
  labelCol: {
    span: 24
  },
  wrapperCol: {
    span: 24
  }
};

export const AddDeliveryAddressModal = ({ show, onClose }: Props): JSX.Element => {
  const { selectedCompanyDataAreaId } = useSelector((state: ReduxState) => state.salesOrders);
  const { values, setValues } = useFormikContext<SalesOrderPayloadModel>();
  const [override, setOverride] = useState(false);

  const [createAddress, { isLoading, error }] = useCreateDeliveryAddressMutation();

  const [form] = Form.useForm<FormModel>();

  const handleFormSubmit = async (formValue: FormModel): Promise<void> => {
    try {
      const data = await createAddress({
        addressData: {
          ...formValue,
          ...{
            dataAreaId: selectedCompanyDataAreaId || '',
            customerId: values.customerId || ''
          }
        }
      }).unwrap();

      setValues((prevState) => ({
        ...prevState,
        deliveryAddressName: form.getFieldValue('description'),
        deliveryAddressLocationId: data.locationId,
        deliveryAddressCity: data.city,
        deliveryAddressCountry: data.countryRegionId,
        deliveryAddressStreet1: data.street,
        deliveryAddressSubdivision: data.state,
        deliveryAddressPostalCode: data.zipCode
      }));
      form.resetFields();
      onClose();
    } catch (err) {
      const error = err as FetchBaseQueryError;

      notification.error({
        message: 'Error',
        description: `${error.data}`,
        className: 'custom-class',
        style: {
          width: 600
        },
        duration: 10
      });
    }
  };

  return (
    <Modal
      getContainer={false}
      centered={true}
      title="Add New Delivery Address"
      open={show}
      confirmLoading={isLoading}
      onCancel={onClose}
      onOk={(): void => form.submit()}
      okText="Add Address"
      okButtonProps={{ icon: <PlusOutlined /> }}>
      {error && <Alert style={{ marginBottom: 20 }} message={(error as StringErrorModel).data ?? 'Server error'} type="error" />}
      <Form
        {...formItemLayout}
        layout="horizontal"
        form={form}
        initialValues={{
          city: '',
          country: 'US',
          description: '',
          state: '',
          street: '',
          suiteNumber: '',
          zipCode: ''
        }}
        onFinish={handleFormSubmit}>
        <Form.Item label="Address Autocomplete">
          <GoogleAutoComplete setAddressValues={form.setFieldValue} />
        </Form.Item>

        <Divider style={{ marginTop: 10, marginBottom: 10 }} />

        <Row justify="end" align="middle" gutter={8}>
          <Col>
            <Typography.Text>Override</Typography.Text>
          </Col>
          <Col>
            <Switch onChange={(checked): void => setOverride(checked)} size="small" />
          </Col>
        </Row>

        <Form.Item label="Description" name="description" rules={[{ required: true, message: 'Please enter a description.' }]}>
          <Input />
        </Form.Item>
        <Form.Item label="Street" name="street" rules={[{ required: true, message: 'Please enter a street.' }]}>
          <Input disabled={!override} />
        </Form.Item>

        <Form.Item label="Suite, Unit, Building" name="suiteNumber">
          <Input />
        </Form.Item>

        <Form.Item label="City" name="city" rules={[{ required: true, message: 'Please enter a city.' }]}>
          <Input disabled={!override} />
        </Form.Item>

        <Form.Item label="State" name="state" rules={[{ required: true, message: 'Please Select a state.' }]}>
          <DeliveryAddressStateSelector disabled={!override} />
        </Form.Item>

        <Form.Item label="Zip Code" name="zipCode" rules={[{ required: true, message: 'Please enter a zip code.' }]}>
          <Input disabled={!override} />
        </Form.Item>

        <Form.Item label="Country" name="country" rules={[{ required: true, message: 'Please Select a country.' }]}>
          <DeliveryAddressCountrySelector disabled={!override} />
        </Form.Item>
      </Form>
    </Modal>
  );
};
